import React, { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

export interface CtaEntry {
  key: string;
  name: string;
  url: string;
}

interface CtaTableProps {
  initialCtas: CtaEntry[];
  onSave: (ctas: CtaEntry[]) => Promise<void>;
  isDarkMode: boolean;
}

const generateKey = (name: string): string => {
  const base = name
    .toLowerCase()
    .replace(/[äöü]/g, (c) => ({ ä: 'ae', ö: 'oe', ü: 'ue' }[c] ?? c))
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return base || `cta_${Date.now()}`;
};

const CtaTable: React.FC<CtaTableProps> = ({ initialCtas, onSave, isDarkMode }) => {
  const [rows, setRows] = useState<CtaEntry[]>(() => initialCtas);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    setRows(initialCtas);
    setSaveSuccess(false);
    setSaveError(null);
  }, [initialCtas]);

  const updateRow = (index: number, field: 'name' | 'url', value: string): void => {
    setSaveSuccess(false);
    setSaveError(null);
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addRow = (): void => {
    setSaveSuccess(false);
    setSaveError(null);
    setRows((prev) => [...prev, { key: `cta_${Date.now()}`, name: '', url: '' }]);
  };

  const deleteRow = (index: number): void => {
    setSaveSuccess(false);
    setSaveError(null);
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async (): Promise<void> => {
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    const normalized = rows
      .filter((r) => r.name.trim() || r.url.trim())
      .map((r) => ({
        key: r.key || generateKey(r.name),
        name: r.name.trim(),
        url: r.url.trim(),
      }));
    try {
      await onSave(normalized);
      setRows(normalized);
      setSaveSuccess(true);
    } catch {
      setSaveError('Links konnten nicht gespeichert werden.');
    } finally {
      setIsSaving(false);
    }
  };

  const border = isDarkMode ? 'border-white/10' : 'border-orange-100';
  const bg = isDarkMode ? 'bg-slate-950/45' : 'bg-orange-50/60';
  const inputClasses = isDarkMode
    ? 'w-full rounded-lg border border-white/10 bg-slate-800 px-2.5 py-1.5 text-sm text-slate-100 placeholder-slate-500 focus:border-emerald-500 focus:outline-none'
    : 'w-full rounded-lg border border-orange-100 bg-white px-2.5 py-1.5 text-sm text-slate-900 placeholder-slate-400 focus:border-emerald-500 focus:outline-none';
  const deleteButtonClasses = isDarkMode
    ? 'rounded-lg p-1.5 text-slate-400 hover:bg-slate-700 hover:text-rose-300 transition-colors'
    : 'rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors';
  const addButtonClasses = isDarkMode
    ? 'flex items-center gap-1.5 rounded-xl border border-white/10 bg-slate-800 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 transition-colors'
    : 'flex items-center gap-1.5 rounded-xl border border-orange-100 bg-white px-3 py-2 text-sm text-slate-600 hover:bg-orange-50 transition-colors';
  const saveButtonClasses = isDarkMode
    ? 'rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-400 disabled:bg-slate-700 disabled:text-slate-300 transition-colors'
    : 'rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:bg-slate-200 disabled:text-slate-500 transition-colors';
  const labelClasses = isDarkMode ? 'text-slate-400' : 'text-slate-500';

  return (
    <div className={`rounded-2xl border px-4 py-4 ${border} ${bg}`}>
      <h3 className={`mb-3 text-sm font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
        Links / Calls to Action
      </h3>

      {rows.length > 0 ? (
        <div className="mb-3 space-y-2">
          <div className={`grid grid-cols-[1fr_1fr_auto] gap-2 px-1 text-xs font-medium ${labelClasses}`}>
            <span>Name</span>
            <span>URL</span>
            <span />
          </div>
          {rows.map((row, index) => (
            <div key={row.key} className="grid grid-cols-[1fr_1fr_auto] items-center gap-2">
              <input
                type="text"
                value={row.name}
                placeholder="Strategieworkshop"
                onChange={(e) => updateRow(index, 'name', e.target.value)}
                className={inputClasses}
              />
              <input
                type="url"
                value={row.url}
                placeholder="https://..."
                onChange={(e) => updateRow(index, 'url', e.target.value)}
                className={inputClasses}
              />
              <button type="button" onClick={() => deleteRow(index)} className={deleteButtonClasses}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className={`mb-3 text-sm ${labelClasses}`}>Noch keine Links hinterlegt.</p>
      )}

      <div className="flex items-center justify-between gap-3">
        <button type="button" onClick={addRow} className={addButtonClasses}>
          <Plus size={14} />
          Link hinzufügen
        </button>

        <button
          type="button"
          onClick={() => { void handleSave(); }}
          disabled={isSaving}
          className={saveButtonClasses}
        >
          {isSaving ? 'Speichert...' : 'Links speichern'}
        </button>
      </div>

      {saveError ? (
        <p className={`mt-2 text-sm ${isDarkMode ? 'text-rose-300' : 'text-rose-600'}`}>{saveError}</p>
      ) : null}
      {saveSuccess ? (
        <p className={`mt-2 text-sm ${isDarkMode ? 'text-emerald-300' : 'text-emerald-600'}`}>Links gespeichert.</p>
      ) : null}
    </div>
  );
};

export default CtaTable;
